import React, { useState, useRef, useEffect } from 'react';
import {
  UserIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { ProfileEditor } from './ProfileEditor';
import { MfaEnrollment } from './MfaEnrollment';
import { mfaService } from '../services/mfaService';
import { useNavigate } from 'react-router-dom';

export const UserMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showMfaEnrollment, setShowMfaEnrollment] = useState(false);
  const [mfaStatus, setMfaStatus] = useState<any>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      checkMfaStatus();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const checkMfaStatus = async () => {
    try {
      const status = await mfaService.checkStatus();
      setMfaStatus(status);
    } catch (error) {
      console.error('[UserMenu] Failed to check MFA status:', error);
      console.error('[UserMenu] Error details:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMfaEnrollmentComplete = async () => {
    setShowMfaEnrollment(false);
    setIsOpen(false);
    await checkMfaStatus();
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 text-white hover:bg-primary-500 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 transition-colors"
        >
          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-sm">
              {user.firstName?.[0]}{user.lastName?.[0]}
            </span>
          </div>
          <div className="text-left hidden md:block">
            <div className="text-sm font-medium">
              {user.firstName} {user.lastName}
            </div>
            <div className="text-xs text-primary-100">
              {user.role}
            </div>
          </div>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-1 z-50 border border-neutral-200">
            <div className="px-4 py-3 border-b border-neutral-200">
              <div className="text-sm font-medium text-neutral-900">
                {user.firstName} {user.lastName}
              </div>
              <div className="text-xs text-neutral-500 truncate">
                {user.email}
              </div>
            </div>

            <div className="py-1">
              <button
                onClick={() => {
                  setShowProfile(true);
                  setIsOpen(false);
                }}
                className="w-full flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                <UserIcon className="h-5 w-5 mr-3 text-neutral-400" />
                Edit Profile
              </button>

              {mfaStatus?.mfaRequired && !mfaStatus?.mfaEnrolled && (
                <button
                  onClick={() => {
                    setShowMfaEnrollment(true);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-warning-700 hover:bg-warning-50 transition-colors"
                >
                  <ShieldCheckIcon className="h-5 w-5 mr-3 text-warning-600" />
                  <span className="flex-1 text-left">Set Up MFA</span>
                  <span className="text-xs bg-warning-100 text-warning-800 px-2 py-0.5 rounded">
                    Required
                  </span>
                </button>
              )}

              {mfaStatus?.mfaEnrolled && (
                <div className="px-4 py-2 text-xs text-neutral-500 flex items-center">
                  <ShieldCheckIcon className="h-4 w-4 mr-2 text-success-600" />
                  MFA Enabled
                </div>
              )}
            </div>

            <div className="border-t border-neutral-200 py-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-neutral-400" />
                Logout
              </button>
            </div>
          </div>
        )}
      </div>

      {showProfile && (
        <ProfileEditor
          isOpen={showProfile}
          onClose={() => setShowProfile(false)}
        />
      )}

      {showMfaEnrollment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <MfaEnrollment
              onComplete={handleMfaEnrollmentComplete}
              onDismiss={() => setShowMfaEnrollment(false)}
            />
          </div>
        </div>
      )}
    </>
  );
};
